import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Sun, Shield, Users, Bed, Bath, Square, ShoppingBag, Utensils, Mountain, GraduationCap, BookOpen, Trophy, TreePine, Heart } from 'lucide-react';
import { ContactCTA } from '../components/ContactCTA';
import { ListingsComingSoon } from '../components/ListingsComingSoon';
const placeholderListings = [{
  id: 1,
  price: '$685,000',
  address: '2847 Grassy Spring Pl',
  area: 'Green Valley Ranch',
  beds: 4,
  baths: 3,
  sqft: '2,650',
  image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2940&auto=format&fit=crop'
}, {
  id: 2,
  price: '$549,000',
  address: '1093 Amber Falls Ln',
  area: 'Anthem',
  beds: 3,
  baths: 2.5,
  sqft: '2,100',
  image: 'https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?q=80&w=2792&auto=format&fit=crop'
}, {
  id: 3,
  price: '$1,450,000',
  address: '15 Hawk Ridge Dr',
  area: 'MacDonald Highlands',
  beds: 5,
  baths: 5,
  sqft: '4,200',
  image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?q=80&w=2940&auto=format&fit=crop'
}];
export function HendersonPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <main className="bg-white" data-id="element-354">
      <section className="relative h-[70vh] min-h-[600px] w-full flex items-center justify-center overflow-hidden" data-id="element-355">
        <div className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat" style={{
        backgroundImage: 'url("https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?q=80&w=2906&auto=format&fit=crop")'
      }} data-id="element-356">
          <div className="absolute inset-0 bg-charcoal/40 mix-blend-multiply" data-id="element-357"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal/60 via-transparent to-charcoal/80" data-id="element-358"></div>
        </div>
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto mt-20" data-id="element-359">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1
        }} data-id="element-360">
            <span className="text-gold uppercase tracking-[0.3em] text-sm font-medium mb-6 block" data-id="element-361">
              Community Spotlight
            </span>
            <h1 className="text-5xl md:text-7xl text-white font-serif mb-6 leading-tight" data-id="element-362">
              Henderson
            </h1>
            <p className="text-lg md:text-xl text-white/90 font-light max-w-2xl mx-auto" data-id="element-363">
              Nevada's second-largest city, consistently ranked among America's
              safest and most livable communities.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-24 px-6 bg-white" data-id="element-364">
        <div className="max-w-7xl mx-auto" data-id="element-365">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-366">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-367">
              <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-6" data-id="element-368">
                A City That <span className="italic text-gold" data-id="element-369">Has It All</span>
              </h2>
              <div className="space-y-4 text-charcoal-light font-light leading-relaxed text-lg" data-id="element-370">
                <p data-id="element-371">
                  Henderson is Nevada's second-largest city with over 320,000
                  residents, and it's consistently ranked among the safest
                  cities in America by Forbes and the FBI. Located just
                  southeast of the Las Vegas Strip, Henderson offers the perfect
                  blend of suburban tranquility and urban convenience.
                </p>
                <p data-id="element-372">
                  The city features a diverse real estate market ranging from
                  $350K starter homes to $10M+ luxury estates in MacDonald
                  Highlands. With excellent schools, world-class healthcare at
                  Henderson Hospital and St. Rose Dominican, and a thriving
                  local economy, Henderson attracts families, professionals, and
                  retirees alike.
                </p>
                <p data-id="element-373">
                  Henderson's Water Street District is undergoing a massive
                  revitalization, bringing new dining, entertainment, and
                  cultural venues to the city's historic downtown core.
                </p>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="grid grid-cols-2 gap-6" data-id="element-374">
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-375">
                <Users className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-376" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-377">
                  320K+
                </h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-378">
                  Residents
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-379">
                <Shield className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-380" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-381">#2</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-382">
                  Safest Large City
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-383">
                <MapPin className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-384" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-385">15m</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-386">
                  To The Strip
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-387">
                <Heart className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-388" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-389">Top</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-390">
                  Most Livable
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Key Neighborhoods */}
      <section className="py-24 bg-warmGray px-6 border-y border-warmGray-dark" data-id="element-391">
        <div className="max-w-7xl mx-auto" data-id="element-392">
          <div className="text-center mb-16" data-id="element-393">
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-394">
              Explore Henderson
            </span>
            <h2 className="text-4xl font-serif text-charcoal" data-id="element-395">
              Premier Neighborhoods
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-396">
            {[{
            name: 'Green Valley Ranch',
            price: '$450K - $900K',
            desc: "One of Henderson's most beloved communities with lush parks, excellent schools, and the upscale District shopping center.",
            img: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2940&auto=format&fit=crop'
          }, {
            name: 'Anthem',
            price: '$400K - $1.2M',
            desc: 'A stunning master-planned community featuring Anthem Country Club, miles of trails, and panoramic valley views.',
            img: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?q=80&w=2940&auto=format&fit=crop'
          }, {
            name: 'MacDonald Highlands',
            price: '$1M - $10M+',
            desc: 'Ultra-luxury gated enclave with DragonRidge Country Club, custom estates, and the most spectacular city views in Vegas.',
            img: 'https://images.unsplash.com/photo-1613490908571-9ce224083933?q=80&w=2838&auto=format&fit=crop'
          }, {
            name: 'Seven Hills',
            price: '$500K - $1.5M',
            desc: 'An exclusive guard-gated community with a Rio Secco Golf Club membership option and elegant Mediterranean homes.',
            img: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=2950&auto=format&fit=crop'
          }, {
            name: 'Lake Las Vegas',
            price: '$400K - $3M+',
            desc: 'A resort community built around a 320-acre private lake with luxury hotels, golf, and waterfront dining.',
            img: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?q=80&w=2906&auto=format&fit=crop'
          }, {
            name: 'Cadence',
            price: '$350K - $600K',
            desc: 'A vibrant newer master-planned community with modern homes, a 50-acre central park, and innovative amenities.',
            img: 'https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?q=80&w=2792&auto=format&fit=crop'
          }].map((n, idx) => <motion.div key={n.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className="bg-white p-6 shadow-sm border border-warmGray-dark" data-id="element-397">
                <img src={n.img} alt={n.name} className="w-full h-48 object-cover mb-4" data-id="element-398" />
                <div className="flex justify-between items-start mb-3" data-id="element-399">
                  <h3 className="text-xl font-serif text-charcoal" data-id="element-400">{n.name}</h3>
                  <span className="text-xs font-medium text-gold bg-gold/10 px-2 py-1 rounded whitespace-nowrap" data-id="element-401">
                    {n.price}
                  </span>
                </div>
                <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-402">
                  {n.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Things To Do */}
      <section className="py-24 px-6 bg-white" data-id="element-403">
        <div className="max-w-7xl mx-auto" data-id="element-404">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-405">
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-406">
              Life in Henderson
            </span>
            <h2 className="text-4xl font-serif text-charcoal" data-id="element-407">Things To Do</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-408">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="group" data-id="element-409">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-410">
                <img src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?q=80&w=2940&auto=format&fit=crop" alt="Shopping" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-411" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-412">
                The District at Green Valley
              </h3>
              <p className="text-charcoal-light font-light text-sm" data-id="element-413">
                An open-air lifestyle center featuring upscale shopping, diverse
                dining, and regular community events in the heart of Henderson.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="group" data-id="element-414">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-415">
                <img src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?q=80&w=2842&auto=format&fit=crop" alt="Outdoor" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-416" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-417">
                Sloan Canyon & Lake Mead
              </h3>
              <p className="text-charcoal-light font-light text-sm" data-id="element-418">
                World-class outdoor recreation including Sloan Canyon's ancient
                petroglyphs, Lake Mead boating, and the River Mountains Loop
                Trail.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="group" data-id="element-419">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-420">
                <img src="https://images.unsplash.com/photo-1566073771259-6a8506099945?q=80&w=2940&auto=format&fit=crop" alt="Resort" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-421" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-422">
                M Resort & Green Valley Ranch
              </h3>
              <p className="text-charcoal-light font-light text-sm" data-id="element-423">
                Two luxury resort-casinos offering fine dining, entertainment,
                spas, and pools — all within minutes of Henderson's best
                neighborhoods.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Schools */}
      <section className="py-24 bg-warmGray px-6 border-t border-warmGray-dark" data-id="element-424">
        <div className="max-w-4xl mx-auto text-center" data-id="element-425">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-426">
            <GraduationCap className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-427" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-428">
              Education
            </span>
            <h2 className="text-4xl font-serif text-charcoal mb-6" data-id="element-429">
              Outstanding Schools
            </h2>
            <p className="text-charcoal-light font-light leading-relaxed text-lg mb-12" data-id="element-430">
              Henderson is home to many of Clark County's highest-performing
              schools. Families enjoy access to top-rated institutions including
              Vanderburg Elementary, Bob Miller Middle School, Coronado High
              School, and Green Valley High School. Private options include
              Henderson International School and the prestigious Bishop Gorman
              High School nearby.
            </p>
          </motion.div>
        </div>
      </section>

      <ListingsComingSoon area="Henderson" data-id="element-431" />

      <ContactCTA data-id="element-432" />
    </main>;
}